import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import axios from "axios";
import { Spinner, Alert, Nav, Container, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSlidersH, faDownload } from "@fortawesome/free-solid-svg-icons";
import ApproximationGraph from "../../components/Graphs/ApproximationGraph";
import ErrorGraph from "../../components/Graphs/ErrorGraph";
import CustomizeTab from "../../components/GraphTabs/CustomizeTab";
import ExportTab from "../../components/GraphTabs/ExportTab";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Color palette generator
export const colorPalettes = { // 20 colors for each option
    Plotly: ["#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#7f7f7f", "#bcbd22", "#aec7e8", "#ffbb78",
        "#98df8a", "#ff9896", "#c7c7c7", "#dbdb8d", "#393b79", "#637939", "#5254a3", "#6baed6", "#e7969c"],
    Muted: ["#4878cf", "#6acc64", "#d65f5f", "#c4ad66", "#77bedb", "#855e42", "#4e88af", "#d384bc", "#7a7a7a", "#8fd175", "#c28fd1",
        "#6a96c3", "#3a5fcd", "#4caf50", "#e53935", "#9c27b0", "#ffb74d", "#4dd0e1", "#795548", "#607d8b"],
    Contrast: ["#000000", "#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4", "#46f0f0", "#f032e6", "#bcf60c", "#800000",
        "#008080", "#000075", "#808000", "#ff4500",  "#daa520", "#00ced1", "#ff69b4", "#7cfc00", "#9400d3"],
    Colorblind: ["#377eb8", "#e41a1c", "#4daf4a", "#984ea3", "#ff7f00", "#ffff33", "#a65628", "#f781bf", "#66c2a5", "#ec7063", "#58d68d",
        "#af7ac5", "#f8c471", "#f1948a", "#3498db", "#e74c3c", "#9b59b6", "#e67e22", "#f1c40f", "#a04000"]
};

const getColorPalette = (palette) => {
    return colorPalettes[palette] || colorPalettes.Plotly;
};

const applyTheme = (layout, theme) => {
    return {
        ...layout,
        paper_bgcolor: theme === "dark" ? "#222" : theme === "plotly" ? "#fff" : "#fff",
        plot_bgcolor: theme === "dark" ? "#333" : theme === "plotly" ? "#fff" : "#fff",
        font: { color: theme === "dark" ? "#fff" : theme === "plotly" ? "#000" : "#333" },
    };
};

const GraphPage = ({ auth, onLogout }) => {
    const { measurementId } = useOutletContext();
    const navigate = useNavigate();
    const [measurement, setMeasurement] = useState(null);
    const [graphData, setGraphData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGraph, setSelectedGraph] = useState("approximation");
    const [errorGraphData, setErrorGraphData] = useState(null);
    const graphRef = useRef(null);
    const [degree, setDegree] = useState(2);
    const [customization, setCustomization] = useState({
        theme: "light",
        lineStyle: "solid",
        markerStyle: "circle",
        colorPalette: "Plotly",
        showGrid: true,
        showLegend: true,
        showRawData: false,
        showFitLines: true,
        showErrorBars: false,
        graphType: "scatter",
        hoverInfo: "x+y+name",
        bubbleSize: 5,
        densityType: "histogram2d",
        facetColumns: 2,
        densityColumns: 2,
    });

    const currentColorPalette = useMemo(
        () => getColorPalette(customization.colorPalette),
        [customization.colorPalette]
    );

    const themeAppliedLayout = useMemo(
        () => (layout) => applyTheme(layout, customization.theme),
        [customization.theme]
    );

    const fetchData = useCallback(async () => {
        if (!measurementId) return;

        try {
            setLoading(true);
            setError(null);

            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${auth.token}` // auth.token for authorization
            };

            const response = await axios.get(`${API_BASE_URL}/measurements/${measurementId}`,
                { headers: headers }
            );
            setMeasurement(response.data);

            // Generate approximation graph first
            const approximationGraphRes = await axios.post(`${API_BASE_URL}/graphs/${measurementId}`,
                { reference_sensor_id: null, degree: degree },
                { headers: headers }
            );

            // Generate error graph after first completes
            const errorGraphRes = await axios.post(`${API_BASE_URL}/graphs/${measurementId}/error`,
                { degree: degree },
                { headers: headers }
            );

            if (!approximationGraphRes.data.approximation_graph_data_by_sensors) {
                throw new Error("Approximation graph data not found in response");
            }

            if (!errorGraphRes.data.approximated_error_graph_data_by_sensors) {
                throw new Error("Error graph data not found in response");
            }

            setGraphData(approximationGraphRes.data.approximation_graph_data_by_sensors);
            setErrorGraphData(errorGraphRes.data.approximated_error_graph_data_by_sensors);
        } catch (err) {
            if (err.response?.status === 401) {
                onLogout();
            } else {
                setError(err.response?.data?.message || err.message || "Failed to load graph data");
            }
        } finally {
            setLoading(false);
        }
    }, [measurementId, degree, auth.token, onLogout]);

    const handleDegreeChange = useCallback((newDegree) => {
        setDegree(newDegree);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRetry = useCallback(() => {
        setError(null);
        fetchData();
    }, [fetchData]);

    const handleCustomizationChange = (key, value) => {
        setCustomization((prev) => ({ ...prev, [key]: value }));
    };

    // Update the handler to work with Nav.Link
    const handleGraphSelection = (selectedKey) => {
        if (selectedKey) {
            setSelectedGraph(selectedKey);
        }
    };

    const graphProps = {
        measurement,
        graphData,
        errorGraphData,
        loading,
        error,
        showGraph: true,
        onRetry: handleRetry,
        customization,
        getColorPalette: () => currentColorPalette,
        applyTheme: themeAppliedLayout,
        onDegreeChange: handleDegreeChange,
        initialDegree: degree,
        auth,
        onLogout
    };

    return (
        <Container fluid className="py-3">
            <Row className="mb-4 g-3">
                <Col lg={9} md={12}>
                    <div className="border p-3 h-100">
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faSlidersH} className="me-2" />
                            Graph Customization
                        </h5>
                        <CustomizeTab
                            customization={customization}
                            onCustomizationChange={handleCustomizationChange}
                            colorPalettes={colorPalettes}
                        />
                    </div>
                </Col>

                <Col lg={3} md={12}>
                    <div className="border p-3 h-100">
                        <h5 className="mb-3">
                            <FontAwesomeIcon icon={faDownload} className="me-2" />
                            Export Data
                        </h5>
                        <ExportTab graphRef={graphRef} measurement={measurement}/>
                    </div>
                </Col>
            </Row>

            <Row className="mb-3">
                <Col>
                    <Nav variant="tabs" defaultActiveKey="approximation">
                        <Nav.Item>
                            <Nav.Link
                                active={selectedGraph === "approximation"}
                                onClick={() => handleGraphSelection("approximation")}
                            >
                                Linear Approximation Graph
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link
                                active={selectedGraph === "error"}
                                onClick={() => handleGraphSelection("error")}
                            >
                                Error Graph
                            </Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Col>
            </Row>

            {loading && (
                <Row className="text-center my-5">
                    <Col>
                        <Spinner animation="border" />
                        <p className="mt-2">Loading graph data...</p>
                    </Col>
                </Row>
            )}

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger">{error}</Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col>
                    <div className="border rounded p-3 w-100" style={{ minHeight: "1000px" }}>
                        {graphData ? (
                            selectedGraph === "approximation" ? (
                                <ApproximationGraph ref={graphRef} {...graphProps} />
                            ) : (
                                <ErrorGraph ref={graphRef} {...graphProps} />
                            )
                        ) : (
                            !loading && !error && (
                                <div className="text-center py-5">
                                    <Alert variant="warning">No graph data available for this measurement</Alert>
                                </div>
                            )
                        )}
                    </div>
                </Col>
            </Row>
        </Container>
    );
};

export default GraphPage;